import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  ZonedDateTime,
} from "@internationalized/date";
import { invariant } from "@pexip-engage/utils";
import { FALLBACK_LNG, getSupportedLng, SupportedLngs } from "@pexip-engage-public/plugin-state";
import { formatDuration, Locale } from "date-fns";
import { da, de, el, enGB, enUS, es, fr, nb, nl } from "date-fns/locale";
import { MeetingType } from "skedify-types";

import { useStateManager } from "~/modules/StateManager.hooks";

export function isBefore(
  date: CalendarDate | ZonedDateTime | CalendarDateTime,
  dateToCompare: CalendarDate | ZonedDateTime | CalendarDateTime,
) {
  return date.compare(dateToCompare) <= 0;
}

export function isAfter(
  date: CalendarDate | ZonedDateTime | CalendarDateTime,
  dateToCompare: CalendarDate | ZonedDateTime | CalendarDateTime,
) {
  return date.compare(dateToCompare) >= 0;
}

export function isBetween(
  date: CalendarDate | ZonedDateTime | CalendarDateTime,
  start: CalendarDate | ZonedDateTime | CalendarDateTime,
  end: CalendarDate | ZonedDateTime | CalendarDateTime,
) {
  return isAfter(date, start) && isBefore(date, end);
}

export function endOfDay(date: ZonedDateTime) {
  return date.set({
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999,
  });
}

export function formatISOWithoutMs(date: Date) {
  return date.toISOString().replace(/\.[0-9]{3}/, "");
}

export function determineTimeZone({
  browserTimeZone,
  customerTimeZone,
  meetingType,
  officeTimeZone,
  timetableTimeZone,
}: {
  officeTimeZone: string | null;
  customerTimeZone: string | null;
  browserTimeZone: string | null;
  timetableTimeZone: string | null;
  meetingType: MeetingType;
}) {
  if (timetableTimeZone) {
    return timetableTimeZone;
  }

  if (meetingType === "office" || meetingType === "on_location") {
    return officeTimeZone ?? customerTimeZone ?? browserTimeZone ?? null;
  }

  return customerTimeZone ?? browserTimeZone ?? null;
}

interface UseTimeZoneArgs {
  officeTimeZone: string | null;
}
/**
 * Correctly resolves the to be used timezone, based on:
 * - `meetingType`
 * - `office.timezone`
 * - `config.customer.timezone`
 * - `config.browser.timeZone`
 * - `getLocalTimeZone()`
 *
 * This is used for displaying the timezone in Timetable.
 * When a slot is chosen, it's set as the `timetable.timeZone`, so subsequent steps can access it more conveniently.
 */
export function useTimeZone(args: UseTimeZoneArgs) {
  const { state } = useStateManager();

  return useBaseTimezone({ ...args, timetableTimeZone: state.timetable.timeZone ?? null });
}

function useBaseTimezone({
  officeTimeZone,
  timetableTimeZone,
}: UseTimeZoneArgs & { timetableTimeZone: string | null }) {
  const { state } = useStateManager();
  const meetingType = state.meetingType;
  invariant(meetingType, "meetingType is required");

  return (
    determineTimeZone({
      meetingType,
      officeTimeZone,
      timetableTimeZone,
      browserTimeZone: state.browser.timeZone ?? null,
      customerTimeZone: state.customer?.timezone ?? state.config.customer?.timezone ?? null,
    }) ?? getLocalTimeZone()
  );
}

export const DayParts = {
  MORNING: "MORNING",
  NOON: "NOON",
  EVENING: "EVENING",
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DayParts = keyof typeof DayParts;

export function getDayPart(startHour: number) {
  if (startHour < 12) return DayParts.MORNING;
  if (startHour < 18) return DayParts.NOON;

  return DayParts.EVENING;
}

export function getDayInterval(start: ZonedDateTime, end: ZonedDateTime) {
  const days: ZonedDateTime[] = [];

  let currentDate = start.copy();

  do {
    days.push(currentDate);
    currentDate = currentDate.add({ days: 1 });
  } while (isAfter(end, currentDate));

  return days;
}

export const DEFAULT_TIMEZONE = "UTC";

export function convertISOStringToDate(isoString: string) {
  return isoString.slice(0, 10);
}

export function millisecondsToTimeUnits(milliseconds: number) {
  let seconds = Math.floor(milliseconds / 1000);
  let minutes = Math.floor(seconds / 60);
  seconds = seconds % 60;
  let hours = Math.floor(minutes / 60);
  minutes = minutes % 60;

  return {
    hours,
    minutes,
    seconds,
  };
}

/**
 * Temporary solution until the following proposal gets released:
 * https://github.com/tc39/proposal-intl-duration-format
 */
export class DurationFormat {
  locale: string;
  // options: { style: "short" };
  constructor(
    locale: string,
    // options: { style: "short" }
  ) {
    this.locale = locale;
    // this.options = options;
  }

  format(duration: { hours: number; minutes: number; seconds: number }) {
    const locale = getSupportedLng(this.locale) ?? FALLBACK_LNG;

    return formatDuration(duration, { locale: localeToDateFnsLocale[locale] });
  }
}

const localeToDateFnsLocale: Record<SupportedLngs, Locale> & { cimode: Locale } = {
  cimode: enGB,
  en: enGB,
  "en-GB": enGB,
  "en-US": enUS,
  de,
  fr,
  nl,
  nb,
  da,
  es,
  el,
};
